<template>
  <div v-if="isSearchableController" class="ui item">
    <div class="ui transparent icon input search-container" :class="{ 'is-searching': isSearching }">
      <input 
        type="text" 
        :placeholder="searchPlaceholder"
        v-model="textobusqueda"
        @keydown.enter="busca"
        @input="handleInput"
        ref="searchInput"
        :disabled="isSearching"
        autocomplete="off"
        aria-label="Campo de búsqueda"
        class="search-input"
      >
      <!-- Icono de búsqueda o spinner -->
      <div class="search-icon-container">
        <i 
          v-if="!isSearching"
          class="search link icon" 
          @click="busca"
          :aria-label="'Buscar'"
          tabindex="0"
          @keydown.enter="busca"
          @keydown.space="busca"
        ></i>
        
        <!-- Spinner personalizado cuando está buscando -->
        <div 
          v-else
          class="custom-spinner"
          :aria-label="'Buscando...'"
        ></div>
      </div>
      
      <!-- Clear button when there's text -->
      <i 
        v-if="textobusqueda && !isSearching" 
        class="times circle link icon clear-icon" 
        @click="clearSearch"
        aria-label="Limpiar búsqueda"
        tabindex="0"
        @keydown.enter="clearSearch"
        @keydown.space="clearSearch"
      ></i>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, nextTick } from 'vue'

export default {
  name: 'BusquedaComponent',
  props: {
    id: {
      type: Number,
      default: -1
    },
    documento: {
      type: String,
      default: "factura",
      validator: (value) => [
        'facturas', 'facturasm', 'albaranes', 'albaranesm', 
        'presupuestos', 'presupuestosm', 'entregasm', 
        'clientesm', 'articulosm'
      ].includes(value)
    }
  },

  setup(props) {
    // Reactive data
    const textobusqueda = ref('')
    const isSearching = ref(false)
    const searchInput = ref(null)

    // Search configuration mapping
    const searchConfig = {
      'facturas': { 
        url: '/facturas',
        placeholder: 'Buscar facturas...',
        param: 'numero'
      },
      'facturasm': { 
        url: '/facturasm',
        placeholder: 'Buscar facturas...',
        param: 'numero'
      },
      'albaranes': { 
        url: '/albaranes',
        placeholder: 'Buscar albaranes...',
        param: 'numero'
      },
      'albaranesm': { 
        url: '/albaranesm',
        placeholder: 'Buscar albaranes...',
        param: 'numero'
      },
      'presupuestos': { 
        url: '/presupuestos',
        placeholder: 'Buscar presupuestos...',
        param: 'numero'
      },
      'presupuestosm': { 
        url: '/presupuestosm',
        placeholder: 'Buscar presupuestos...',
        param: 'numero'
      },
      'entregasm': { 
        url: '/entregasm',
        placeholder: 'Buscar entregas...',
        param: 'numero'
      },
      'clientesm': { 
        url: '/clientesm',
        placeholder: 'Buscar clientes...',
        param: 'numero'
      },
      'articulosm': { 
        url: '/articulosm',
        placeholder: 'Buscar artículos...',
        param: 'numero'
      }
    }

    // Computed properties
    const currentConfig = computed(() => {
      return searchConfig[props.documento] || null
    })

    const isSearchableController = computed(() => {
      return currentConfig.value !== null
    })

    const searchPlaceholder = computed(() => {
      return currentConfig.value?.placeholder || 'Buscar...'
    })

    // Methods
    const busca = async () => {
      if (isSearching.value || !currentConfig.value) return

      const searchTerm = textobusqueda.value.trim()
      const config = currentConfig.value

      try {
        isSearching.value = true

        // Build URL with search parameter
        let targetUrl = config.url
        
        if (searchTerm) {
          const separator = config.url.includes('?') ? '&' : '?'
          targetUrl = `${config.url}${separator}${config.param}=${encodeURIComponent(searchTerm)}`
        }

        // Small delay for UX (shows loading state)
        await new Promise(resolve => setTimeout(resolve, 200))
        
        // Navigate to search results
        window.location.href = targetUrl

      } catch (error) {
        console.error('Error during search:', error)
        isSearching.value = false
      }
    }

    const clearSearch = () => {
      textobusqueda.value = ''
      
      // For articles, navigate to clean URL when clearing
      if (props.documento === 'articulosm') {
        window.location.href = '/articulosm'
      }
      
      // Focus back to input
      nextTick(() => {
        if (searchInput.value) {
          searchInput.value.focus()
        }
      })
    }

    const handleInput = (event) => {
      // Could add debounced search suggestions here in the future
      const value = event.target.value
      
      // Remove any unwanted characters for security
      textobusqueda.value = value.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    }

    const focusSearch = () => {
      if (searchInput.value) {
        searchInput.value.focus()
      }
    }

    // Lifecycle
    onMounted(() => {
      console.log('🔍 Búsqueda component mounted for documento:', props.documento)
      
      // Add global keyboard shortcut (Ctrl+K or Cmd+K)
      const handleKeydown = (event) => {
        if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
          event.preventDefault()
          focusSearch()
        }
      }
      
      document.addEventListener('keydown', handleKeydown)
      
      // Cleanup on unmount
      return () => {
        document.removeEventListener('keydown', handleKeydown)
      }
    })

    return {
      textobusqueda,
      isSearching,
      searchInput,
      isSearchableController,
      searchPlaceholder,
      busca,
      clearSearch,
      handleInput,
      focusSearch
    }
  }
}
</script>

<style scoped>
/* Ensure proper visibility in menu */
.ui.item {
  padding: 0.5em !important;
}

/* Container styles - NO rotation here */
.ui.transparent.input.search-container {
  background: rgba(255, 255, 255, 0.9) !important;
  border: 1px solid rgba(34, 36, 38, 0.15) !important;
  border-radius: 0.28571429rem !important;
  position: relative !important;
}

/* IMPORTANT: Override any Semantic UI loading/spinning styles on container */
.ui.transparent.input.search-container.is-searching {
  /* Force no animation on container */
  animation: none !important;
  transform: none !important;
  /* Remove any focus/active styles that might highlight the border */
  box-shadow: none !important;
  border-color: rgba(34, 36, 38, 0.15) !important;
}

.ui.transparent.input input.search-input {
  background: transparent !important;
  border: none !important;
  padding: 0.67857143em 2.67142857em 0.67857143em 1em !important;
  color: rgba(0, 0, 0, 0.87) !important;
  font-size: 0.92857143em !important;
  width: 250px !important;
}

.ui.transparent.input input.search-input::placeholder {
  color: rgba(0, 0, 0, 0.4) !important;
}

.ui.transparent.input input.search-input:focus {
  background: white !important;
  border-color: #85b7d9 !important;
  outline: none !important;
  box-shadow: 0 0 0 0.2em rgba(34, 133, 208, 0.15) !important;
}

/* Search icon container */
.search-icon-container {
  position: absolute !important;
  right: 0.5em !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
  width: 1.2em !important;
  height: 1.2em !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

/* Search icon - Default state */
.search.icon {
  color: rgba(0, 0, 0, 0.6) !important;
  cursor: pointer !important;
  transition: color 0.2s ease !important;
  /* Remove any positioning since it's in a container now */
  position: static !important;
  transform: none !important;
  margin: 0 !important;
}

.search.icon:hover {
  color: #2185d0 !important;
}

/* Custom spinner - completamente independiente de Semantic UI */
.custom-spinner {
  width: 1em !important;
  height: 1em !important;
  border: 2px solid rgba(0, 0, 0, 0.2) !important;
  border-top: 2px solid #2185d0 !important;
  border-radius: 50% !important;
  animation: custom-spin 1s linear infinite !important;
}

@keyframes custom-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Clear icon */
.clear-icon {
  position: absolute !important;
  right: 2em !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
  z-index: 2 !important;
  opacity: 0.7 !important;
  transition: opacity 0.2s ease, color 0.2s ease !important;
  cursor: pointer !important;
}

.clear-icon:hover {
  opacity: 1 !important;
  color: #db2828 !important;
}

/* Focus styles */
.search.icon:focus,
.clear-icon:focus {
  outline: 2px solid #2185d0 !important;
  outline-offset: 2px !important;
}

/* Disabled state - subtle opacity on input */
.search-container.is-searching input.search-input {
  opacity: 0.7 !important;
}

/* Override ALL Semantic UI loading/spinning classes */
.ui.input.loading,
.ui.input .loading.icon,
.ui.loading.input,
.loading.icon {
  animation: none !important;
  transform: none !important;
  box-shadow: none !important;
  border-color: inherit !important;
}

/* Prevent any focus effects during search */
.search-container.is-searching,
.search-container.is-searching:focus-within {
  box-shadow: none !important;
  border-color: rgba(34, 36, 38, 0.15) !important;
}

/* Responsive improvements */
@media (max-width: 768px) {
  .ui.transparent.input input.search-input {
    font-size: 16px !important;
    width: 200px !important;
  }
}

/* Better contrast for secondary menu */
.ui.secondary.menu .ui.transparent.input.search-container {
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(34, 36, 38, 0.2) !important;
}

.ui.secondary.menu .ui.transparent.input input.search-input {
  color: rgba(0, 0, 0, 0.9) !important;
}

.ui.secondary.menu .ui.transparent.input input.search-input::placeholder {
  color: rgba(0, 0, 0, 0.5) !important;
}

/* Ensure the secondary menu search container doesn't highlight during search */
.ui.secondary.menu .search-container.is-searching {
  border-color: rgba(34, 36, 38, 0.2) !important;
  box-shadow: none !important;
}
</style>